import { Component, OnDestroy, OnInit } from '@angular/core';
import dayjs from 'dayjs';
import * as ibantools from 'ibantools';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Client, ClientBankAccount } from 'src/app/api/models';
import { ClientsService } from 'src/app/api/services';
import { ClientPeerService } from '../../service/client-peer.service';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {ValidatorService} from 'angular-iban';
import { vopHolderName, vopResultLabel } from 'src/app/shared/vop/vop-result';

@Component({
  selector: 'app-client-bank-accounts',
  templateUrl: './client-bank-accounts.component.html',
  styleUrls: ['./client-bank-accounts.component.scss']
})
export class ClientBankAccountsComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  adding = false;

  client$: Observable<Client>;
  columns = ['iban', 'bic', 'is_verified', 'created_at', 'actions'];
  /** The columns with the Verification of Payee one beside ownership confirmed. */
  private readonly vopColumns = ['iban', 'bic', 'is_verified', 'vop', 'created_at', 'actions'];

  public readonly vopLabel = vopResultLabel;
  public readonly vopHolder = vopHolderName;

  form: UntypedFormGroup;

  constructor(public dialog: MatDialog,
    private builder: UntypedFormBuilder,
    private clientPeerService: ClientPeerService,
    private clientsService: ClientsService) {

    this.form = this.builder.group({
      iban: [null, [Validators.required, ValidatorService.validateIban]],
      bic: [null],
      is_verified: [false],
    });
  }

  /**
   * The table's columns. The Verification of Payee column shows once any of
   * the client's accounts carries a result: the API sends none while VoP is
   * off in the environment, and then the column would only say so.
   */
  columnsFor(client: Client): string[] {
    return client.bank_accounts?.some(a => a.vop_result) ? this.vopColumns : this.columns;
  }

  ngOnInit(): void {
    this.client$ = this.clientPeerService.get().pipe(takeUntil(this.ngDestroy$));
  }

  public add() {
    this.adding = true;
  }

  closeAdd() {
    this.adding = false;
    this.form.reset();
  }

  createAccount(client: Client) {
    const account = <ClientBankAccount>{};
    Object.assign(account, this.form.value);
    if (this.form.value.is_verified) {
      account.verified_at = dayjs().toISOString();
    }
    account.iban = ibantools.electronicFormatIBAN(account.iban);

    this.clientsService.createClientBankAccount({
      client_id: client.id,
      body: account
    }).pipe(take(1))
    .subscribe(() => {
      this.clientPeerService.refresh();
      this.closeAdd();
    });
  }

  verify(client: Client, account: ClientBankAccount) {
    account.verified_at = dayjs().toISOString();
    this.clientsService.updateClientBankAccount({
      client_id: client.id,
      id: account.id,
      body: account
    }).pipe(take(1))
    .subscribe(() => {
      this.clientPeerService.refresh();
    });
  }

  unverify(client: Client, account: ClientBankAccount) {
    account.verified_at = null;
    this.clientsService.updateClientBankAccount({
      client_id: client.id,
      id: account.id,
      body: account
    }).pipe(take(1))
    .subscribe(() => {
      this.clientPeerService.refresh();
    });
  }

  confirmDelete(client: Client, account: ClientBankAccount) {
    const dialogData = new ConfirmationDialogModel('Delete Bank Account?', `Deleting ${account.iban} bank account will remove it from ${client.name} accounts.`, "Yes, delete account", "No, keep account");
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.clientsService.deleteClientBankAccount({client_id: client.id, id: account.id})
          .pipe(take(1))
          .subscribe(() => {
            this.clientPeerService.refresh();
          });
      }
    });
  }

  accountIcon(a: ClientBankAccount): string {
    if (a.verified_at) {
      return "check_circle_outline";
    }
    //return "pending_actions";
    return "unpublished";
  }

  ngOnDestroy() {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }
}
